// ============================================================================
// AI EXTRACTION UTILITIES FOR LIABILITY FORMS
// ============================================================================

// Currency symbol to code mapping
const CURRENCY_SYMBOL_MAP = {
  'USD': ['$', '¢'],
  'INR': ['₹'],
  'EUR': ['€'],
  'GBP': ['£', '₤'],
  'JPY': ['¥'],
  'KRW': ['₩'],
  'RUB': ['₽'],
  'BTC': ['₿', '฿'],
  'ETH': ['Ξ'],
  'CHF': ['₣'],
  'NGN': ['₦'],
  'ILS': ['₪'],
  'VND': ['₫'],
  'LAK': ['₭'],
  'MNT': ['₮'],
  'PHP': ['₱'],
  'PYG': ['₲'],
  'UAH': ['₴'],
  'GHS': ['₵'],
  'KZT': ['₸'],
  'TRY': ['₺'],
  'AZN': ['₼'],
  'BDT': ['৳'],
  'GEL': ['ლ', '₾'],
};

// Get currency from symbol
export const getCurrencyFromSymbol = (symbol) => {
  for (const [currency, symbols] of Object.entries(CURRENCY_SYMBOL_MAP)) {
    if (symbols.includes(symbol)) {
      return currency;
    }
  }
  return null;
};

// Detect currency from symbol in extracted data
export const detectCurrencyFromAmount = (amountValue) => {
  if (!amountValue || typeof amountValue !== 'string') return null;
  
  for (let i = 0; i < amountValue.length; i++) {
    const detected = getCurrencyFromSymbol(amountValue[i]);
    if (detected) {
      return detected;
    }
  }
  return null;
};

// Extract currency from various fields and detect from amount values
export const extractAndSetCurrency = (extractedData, setFieldValue) => {
  if (!extractedData || !setFieldValue) return null;
  
  const currencyFieldNames = [
    'currency', 'currencyCode', 'purchaseCurrency', 'loanCurrency',
    'amountCurrency', 'originalLoanAmountCurrency', 'outstandingAmountCurrency',
    'creditLimitCurrency', 'totalAmountCurrency', 'premiumAmountCurrency',
  ];
  
  for (const fieldName of currencyFieldNames) {
    if (extractedData[fieldName]) {
      setFieldValue(fieldName, extractedData[fieldName]);
      return extractedData[fieldName];
    }
  }
  
  const amountFieldNames = [
    'originalLoanAmount', 'outstandingAmount', 'currentBalance', 'creditLimit',
    'totalAmount', 'premiumAmount', 'monthlyRepayment', 'amountDue',
    'minimumPayment', 'overdraftLimit', 'remainingBalance', 'nextPaymentAmount',
    'deductibleAmount', 'originalAmount',
  ];
  
  for (const fieldName of amountFieldNames) {
    if (extractedData[fieldName]) {
      const detectedCurrency = detectCurrencyFromAmount(extractedData[fieldName]);
      if (detectedCurrency) {
        setFieldValue('currency', detectedCurrency);
        setFieldValue('currencyCode', detectedCurrency);
        return detectedCurrency;
      }
    }
  }
  return null;
};

// Normalization functions for AI-extracted liability data
export const normalizeLiabilityFrequency = (value) => {
  if (!value) return '';
  const normalized = value.toLowerCase().trim();
  const frequencyMap = {
    'montly': 'monthly', 'month': 'monthly', 'monthely': 'monthly',
    'quarterly': 'quarterly', 'quarter': 'quarterly',
    'yearly': 'yearly', 'year': 'yearly', 'annual': 'yearly',
    'halfyearly': 'halfyearly', 'half yearly': 'halfyearly',
    'weekly': 'weekly', 'week': 'weekly',
    'daily': 'daily', 'day': 'daily'
  };
  return frequencyMap[normalized] || normalized;
};

export const normalizeRepaymentFrequency = (value) => {
  if (!value) return '';
  const normalized = value.toLowerCase().trim();
  const frequencyMap = {
    'monthly': 'monthly', 'every month': 'monthly', 'per month': 'monthly',
    'quarterly': 'quarterly', 'every quarter': 'quarterly',
    'yearly': 'yearly', 'annual': 'yearly', 'annually': 'yearly',
    'bi-weekly': 'bi-weekly', 'biweekly': 'bi-weekly', 'fortnightly': 'bi-weekly',
    'weekly': 'weekly', 'every week': 'weekly',
    'one-time': 'one-time', 'onetime': 'one-time', 'lump sum': 'one-time'
  };
  return frequencyMap[normalized] || normalized;
};

export const normalizeTaxType = (value) => {
  if (!value) return '';
  const normalized = value.toLowerCase().trim();
  const typeMap = {
    'income': 'income', 'income tax': 'income', 'federal': 'income',
    'property': 'property', 'property tax': 'property', 'real estate': 'property',
    'sales': 'sales', 'sales tax': 'sales', 'vat': 'sales',
    'corporate': 'corporate', 'business': 'corporate',
    'capital': 'capital gains', 'capital gains': 'capital gains',
    'estate': 'estate', 'inheritance': 'estate',
    'gift': 'gift', 'gift tax': 'gift'
  };
  return typeMap[normalized] || normalized;
};

export const normalizeLoanType = (value) => {
  if (!value) return '';
  const normalized = value.toLowerCase().trim();
  const typeMap = {
    'personal': 'personal', 'consumer': 'personal',
    'home': 'home', 'home loan': 'home', 'housing': 'home',
    'fixed': 'fixed', 'fixed rate': 'fixed',
    'variable': 'variable', 'adjustable': 'variable', 'arm': 'variable',
    'interest-only': 'interest-only', 'interest only': 'interest-only',
    'secured': 'secured', 'unsecured': 'unsecured',
    'consolidation': 'consolidation', 'debt consolidation': 'consolidation'
  };
  return typeMap[normalized] || normalized;
};

export const normalizeVehicleLoanType = (value) => {
  if (!value) return '';
  const normalized = value.toLowerCase().trim();
  const typeMap = {
    'car': 'car', 'auto': 'car', 'automobile': 'car',
    'motorcycle': 'motorcycle', 'bike': 'motorcycle', 'scooter': 'motorcycle',
    'truck': 'truck', 'suv': 'suv', 'van': 'van',
    'boat': 'boat', 'marine': 'boat',
    'new': 'new', 'used': 'used', 'pre-owned': 'used', 'secondhand': 'used'
  };
  return typeMap[normalized] || normalized;
};

export const normalizeBNPLProvider = (value) => {
  if (!value) return '';
  const normalized = value.toLowerCase().trim();
  const providerMap = {
    'klarna': 'klarna', 'afterpay': 'afterpay', 'affirm': 'affirm',
    'sezzle': 'sezzle', 'paypal': 'paypal', 'pay in 4': 'paypal',
    'apple': 'apple pay', 'apple pay': 'apple pay',
    'google': 'google pay', 'google pay': 'google pay',
    'amazon': 'amazon pay', 'amazon pay': 'amazon pay',
    'cash app': 'cash app', 'cashapp': 'cash app',
    'meta': 'meta pay', 'meta pay': 'meta pay', 'facebook': 'meta pay'
  };
  return providerMap[normalized] || normalized;
};

export const normalizeInsuranceType = (value) => {
  if (!value) return '';
  const normalized = value.toLowerCase().trim();
  const typeMap = {
    'health': 'health', 'medical': 'health', 'medical insurance': 'health',
    'life': 'life', 'life insurance': 'life',
    'car': 'auto', 'vehicle': 'auto', 'auto': 'auto', 'auto insurance': 'auto',
    'home': 'home', 'house': 'home', 'property': 'home', 'home insurance': 'home',
    'travel': 'travel', 'trip': 'travel',
    'dental': 'dental', 'dental insurance': 'dental',
    'vision': 'vision', 'eye': 'vision', 'eye insurance': 'vision',
    'disability': 'disability', 'income protection': 'disability',
    'critical': 'critical illness', 'critical illness': 'critical illness',
    'umbrella': 'umbrella',
  };
  return typeMap[normalized] || normalized;
};

export const normalizeDebtCategory = (value) => {
  if (!value) return '';
  const normalized = value.toLowerCase().trim();
  const categoryMap = {
    'credit card': 'credit card', 'creditcard': 'credit card', 'cc': 'credit card',
    'personal loan': 'personal loan', 'personalloan': 'personal loan',
    'payday': 'payday loan', 'payday loan': 'payday loan',
    'medical': 'medical debt', 'medical debt': 'medical debt',
    'student': 'student loan', 'student loan': 'student loan', 'education': 'student loan',
    'auto': 'auto loan', 'auto loan': 'auto loan', 'car loan': 'auto loan',
    'mortgage': 'mortgage', 'home loan': 'mortgage',
    'business': 'business loan', 'business loan': 'business loan',
    'family': 'family loan', 'family debt': 'family loan',
    'friend': 'friend loan', 'loan from friend': 'friend loan',
    'other': 'other', 'miscellaneous': 'other', 'misc': 'other',
  };
  return categoryMap[normalized] || normalized;
};

// Main mapping of extracted keys to form field names
export const LIABILITY_EXTRACTION_KEY_MAP = {
  // General fields
  'liabilityType': 'liability',
  'liability_type': 'liability',
  'type': 'liability',
  'name': 'name',
  'lenderName': 'lenderName',
  'lender_name': 'lenderName',
  'bankName': 'bankName',
  'bank_name': 'bankName',
  
  // Amount fields
  'originalLoanAmount': 'originalLoanAmount',
  'loanAmount': 'originalLoanAmount',
  'loan_amount': 'originalLoanAmount',
  'outstandingAmount': 'outstandingAmount',
  'outstanding_amount': 'outstandingAmount',
  'currentBalance': 'currentBalance',
  'current_balance': 'currentBalance',
  'remainingBalance': 'outstandingAmount',
  'remaining_balance': 'outstandingAmount',
  
  // Rate and date fields
  'interestRate': 'interestRate',
  'interest_rate': 'interestRate',
  'apr': 'interestRate',
  'annualPercentageRate': 'interestRate',
  'loanStartDate': 'loanStartDate',
  'loan_start_date': 'loanStartDate',
  'startDate': 'loanStartDate',
  'start_date': 'loanStartDate',
  'maturityDate': 'maturityDate',
  'maturity_date': 'maturityDate',
  'endDate': 'maturityDate',
  'end_date': 'maturityDate',
  
  // Repayment fields
  'monthlyRepayment': 'monthlyRepayment',
  'monthly_payment': 'monthlyRepayment',
  'monthlyPayment': 'monthlyRepayment',
  'emi': 'monthlyRepayment',
  'repaymentFrequency': 'repaymentFrequency',
  'repayment_frequency': 'repaymentFrequency',
  'paymentFrequency': 'repaymentFrequency',
  
  // Credit card fields
  'creditLimit': 'creditLimit',
  'credit_limit': 'creditLimit',
  'minimumPayment': 'minimumPayment',
  'minimum_payment': 'minimumPayment',
  'statementDate': 'statementDate',
  'statement_date': 'statementDate',
  'paymentDueDate': 'paymentDueDate',
  'payment_due_date': 'paymentDueDate',
  'due_date': 'paymentDueDate',
  
  // BNPL fields
  'merchantName': 'merchantName',
  'merchant_name': 'merchantName',
  'serviceProvider': 'serviceProvider',
  'service_provider': 'serviceProvider',
  'provider': 'serviceProvider',
  'purchaseDescription': 'purchaseDescription',
  'totalAmount': 'totalAmount',
  'total_amount': 'totalAmount',
  'remainingPayments': 'remainingPayments',
  'remaining_payments': 'remainingPayments',
  'paymentsLeft': 'remainingPayments',
  'nextPaymentDate': 'nextPaymentDate',
  'next_payment_date': 'nextPaymentDate',
  'nextPaymentAmount': 'nextPaymentAmount',
  'next_payment_amount': 'nextPaymentAmount',
  
  // Insurance fields
  'insuranceCompany': 'insuranceCompany',
  'insurance_company': 'insuranceCompany',
  'policyNumber': 'policyNumber',
  'policy_number': 'policyNumber',
  'insuranceType': 'insuranceType',
  'insurance_type': 'insuranceType',
  'premiumAmount': 'premiumAmount',
  'premium_amount': 'premiumAmount',
  'policyStartDate': 'policyStartDate',
  'policy_start_date': 'policyStartDate',
  'policyEndDate': 'policyEndDate',
  'policy_end_date': 'policyEndDate',
  'deductibleAmount': 'deductibleAmount',
  'deductible_amount': 'deductibleAmount',
  
  // Tax fields
  'taxType': 'taxType',
  'tax_type': 'taxType',
  'taxYearPeriod': 'taxYearPeriod',
  'tax_year_period': 'taxYearPeriod',
  'amountDue': 'amountDue',
  'amount_due': 'amountDue',
  'dueDate': 'dueDate',
  'penalty': 'penalty',
  'interestCharges': 'interestCharges',
  'interest_charges': 'interestCharges',
  'paymentStatus': 'paymentStatus',
  
  // Vehicle fields
  'vehicleName': 'vehicleName',
  'vehicle_name': 'vehicleName',
  'vehicleType': 'vehicleType',
  'vehicle_type': 'vehicleType',
  'registrationNumber': 'registrationNumber',
  'registration_number': 'registrationNumber',
  
  // Personal debt fields
  'originalAmount': 'originalAmount',
  'original_amount': 'originalAmount',
  'category': 'category',
  
  // Other fields
  'loanTerm': 'loanTerm',
  'loan_term': 'loanTerm',
  'loanTermMonths': 'loanTerm',
  'loan_term_months': 'loanTerm',
  'loanPurpose': 'loanPurpose',
  'loan_purpose': 'loanPurpose',
  'overdraftLimit': 'overdraftLimit',
  'overdraft_limit': 'overdraftLimit',
  'currentOverdraftAmount': 'currentOverdraftAmount',
  'overdraftFee': 'overdraftFee',
  'overdraft_fee': 'overdraftFee',
  'overdraftStartDate': 'overdraftStartDate',
  'issuer': 'issuer',
};

// Helper function to normalize extracted data structure
export const normalizeLiabilityExtractedItem = (raw, liabilityType) => {
  if (!raw || typeof raw !== 'object') return null;
  
  const normalized = {};
  const data = raw.generalDetails || raw.details || raw;
  
  // Apply key mapping
  for (const [aiKey, formKey] of Object.entries(LIABILITY_EXTRACTION_KEY_MAP)) {
    if (data[aiKey] !== undefined) {
      normalized[formKey] = data[aiKey];
    }
  }
  
  // Copy any unmapped fields that might be useful
  for (const key of Object.keys(data)) {
    if (normalized[key] === undefined) {
      normalized[key] = data[key];
    }
  }
  
  // Add liability type if not present
  if (!normalized.liability) {
    normalized.liability = liabilityType;
  }
  
  return normalized;
};

// Helper to get extracted items as array (for multi-record scenarios)
export const getExtractedLiabilityArray = (extractedData, liabilityType) => {
  if (!extractedData) return [];
  
  // If data is already an array
  if (Array.isArray(extractedData)) {
    return extractedData.map(item => normalizeLiabilityExtractedItem(item, liabilityType));
  }
  
  // If data is a single object with nested array
  const items = extractedData.items || extractedData.liabilities || extractedData.records || [];
  if (Array.isArray(items)) {
    return items.map(item => normalizeLiabilityExtractedItem(item, liabilityType));
  }
  
  // Single item
  const normalized = normalizeLiabilityExtractedItem(extractedData, liabilityType);
  return normalized ? [normalized] : [];
};

// Mapping function for each liability type
const extractionMappers = {
  mortgage: (extractedData) => {
    const details = extractedData.mortgageDetails || {};
    return {
      name: details.propertyAddress ? `${details.lenderName || 'Mortgage'} - ${details.propertyAddress}` : extractedData.name || '',
      lenderName: details.lenderName || extractedData.lenderName || '',
      originalLoanAmount: details.originalLoanAmount || extractedData.originalLoanAmount || '',
      outstandingAmount: details.outstandingAmount || extractedData.outstandingAmount || extractedData.currentBalance || '',
      interestRate: details.interestRate || extractedData.interestRate || '',
      loanStartDate: details.loanStartDate || extractedData.loanStartDate || extractedData.startDate || '',
      maturityDate: details.maturityDate || extractedData.maturityDate || extractedData.endDate || '',
      monthlyRepayment: details.monthlyRepayment || extractedData.monthlyPayment || '',
      loanTerm: details.loanTermMonths || extractedData.loanTermMonths || extractedData.loanTerm || '',
      loanType: normalizeLoanType(details.loanType || extractedData.loanType),
      repaymentFrequency: normalizeRepaymentFrequency(details.repaymentFrequency || extractedData.repaymentFrequency),
      propertyAddress: details.propertyAddress || extractedData.propertyAddress || '',
      propertyType: details.propertyType || extractedData.propertyType || '',
    };
  },
  'vehicle loan': (extractedData) => {
    const details = extractedData.vehicleLoanDetails || {};
    return {
      name: details.vehicleName || extractedData.vehicleName || '',
      lenderName: details.lenderName || extractedData.lenderName || '',
      originalLoanAmount: details.originalLoanAmount || extractedData.originalLoanAmount || '',
      outstandingAmount: details.outstandingAmount || extractedData.outstandingAmount || '',
      interestRate: details.interestRate || extractedData.interestRate || '',
      loanStartDate: details.loanStartDate || extractedData.loanStartDate || '',
      maturityDate: details.maturityDate || extractedData.maturityDate || '',
      monthlyRepayment: details.monthlyRepayment || extractedData.monthlyPayment || '',
      loanTerm: details.loanTermMonths || extractedData.loanTermMonths || '',
      loanType: normalizeVehicleLoanType(details.vehicleType || extractedData.vehicleType),
      vehicleName: details.vehicleName || extractedData.vehicleName || '',
      vehicleType: normalizeVehicleLoanType(details.vehicleType || extractedData.vehicleType),
      registrationNumber: details.registrationNumber || extractedData.registrationNumber || ''
    };
  },
  loan: (extractedData) => {
    const details = extractedData.generalLoanDetails || {};
    return {
      name: details.loanPurpose || extractedData.loanPurpose || extractedData.name || '',
      lenderName: details.lenderName || extractedData.lenderName || '',
      originalLoanAmount: details.originalAmount || extractedData.originalAmount || '',
      outstandingAmount: details.outstandingAmount || extractedData.outstandingAmount || '',
      interestRate: details.interestRate || extractedData.interestRate || '',
      loanStartDate: details.loanStartDate || extractedData.loanStartDate || extractedData.startDate || '',
      maturityDate: details.maturityDate || extractedData.maturityDate || extractedData.endDate || '',
      nextPaymentDate: details.nextPaymentDate || extractedData.nextPaymentDate || '',
      loanTerm: details.loanTermMonths || extractedData.loanTermMonths || '',
      loanType: normalizeLoanType(details.loanType || extractedData.loanType),
      loanPurpose: details.loanPurpose || extractedData.loanPurpose || '',
      repaymentFrequency: normalizeRepaymentFrequency(details.repaymentFrequency || extractedData.repaymentFrequency)
    };
  },
  'credit card': (extractedData) => {
    const details = extractedData.creditCardDetails || {};
    return {
      name: details.cardName || extractedData.cardName || extractedData.name || '',
      issuer: details.issuer || extractedData.issuer || extractedData.bankName || '',
      creditLimit: details.creditLimit || extractedData.creditLimit || '',
      currentBalance: details.currentBalance || extractedData.currentBalance || '',
      annualPercentageRate: details.annualPercentageRate || extractedData.annualPercentageRate || extractedData.interestRate || '',
      minimumPayment: details.minimumPayment || extractedData.minimumPayment || '',
      repaymentFrequency: normalizeRepaymentFrequency(details.repaymentFrequency || extractedData.repaymentFrequency),
      statementDate: details.statementDate || extractedData.statementDate || '',
      paymentDueDate: details.paymentDueDate || extractedData.paymentDueDate || extractedData.dueDate || '',
      cardName: details.cardName || extractedData.cardName || ''
    };
  },
  overdraft: (extractedData) => {
    const details = extractedData.overdraftDetails || {};
    return {
      name: details.accountName || extractedData.accountName || extractedData.name || '',
      bankName: details.bankName || extractedData.bankName || '',
      overdraftLimit: details.overdraftLimit || extractedData.overdraftLimit || '',
      currentOverdraftAmount: details.currentOverdraftAmount || extractedData.currentOverdraftAmount || extractedData.currentBalance || '',
      interestRate: details.interestRate || extractedData.interestRate || '',
      overdraftFee: details.overdraftFee || extractedData.overdraftFee || '',
      overdraftStartDate: details.overdraftStartDate || extractedData.overdraftStartDate || '',
      repaymentFrequency: normalizeRepaymentFrequency(details.repaymentFrequency || extractedData.repaymentFrequency)
    };
  },
  'tax payable': (extractedData) => {
    const details = extractedData.taxPayableDetails || {};
    return {
      name: details.taxType ? `${details.taxType} Tax ${details.taxYearPeriod || ''}` : extractedData.name || 'Tax Payment',
      taxType: normalizeTaxType(details.taxType || extractedData.taxType),
      taxYearPeriod: details.taxYearPeriod || extractedData.taxYearPeriod || '',
      amountDue: details.amountDue || extractedData.amountDue || extractedData.originalAmount || '',
      dueDate: details.dueDate || extractedData.dueDate || '',
      penalty: details.penalty || extractedData.penalty || '',
      interestCharges: details.interestCharges || extractedData.interestCharges || '',
      paymentStatus: details.paymentStatus || extractedData.paymentStatus || 'pending'
    };
  },
  bnpl: (extractedData) => {
    const details = extractedData.bnplDetails || {};
    return {
      name: details.purchaseDescription || extractedData.purchaseDescription || extractedData.name || 'BNPL Purchase',
      merchantName: details.merchantName || extractedData.merchantName || '',
      serviceProvider: normalizeBNPLProvider(details.serviceProvider || extractedData.serviceProvider),
      purchaseDescription: details.purchaseDescription || extractedData.purchaseDescription || '',
      totalAmount: details.totalAmount || extractedData.totalAmount || extractedData.originalAmount || '',
      remainingBalance: details.remainingBalance || extractedData.remainingBalance || '',
      nextPaymentDate: details.nextPaymentDate || extractedData.nextPaymentDate || '',
      nextPaymentAmount: details.nextPaymentAmount || extractedData.nextPaymentAmount || '',
      remainingPayments: details.remainingPayments || extractedData.remainingPayments || '',
      interestFees: details.interestFees || extractedData.interestFees || 0,
      startDate: details.startDate || extractedData.startDate || '',
      endDate: details.endDate || extractedData.endDate || ''
    };
  },
  'insurance premium': (extractedData) => {
    const details = extractedData.insurancePremiumDetails || {};
    return {
      name: details.insuranceCompany ? `${details.insuranceCompany} Premium` : extractedData.name || 'Insurance Premium',
      insuranceCompany: details.insuranceCompany || extractedData.insuranceCompany || '',
      policyNumber: details.policyNumber || extractedData.policyNumber || '',
      insuranceType: normalizeInsuranceType(details.insuranceType || extractedData.insuranceType),
      premiumAmount: details.premiumAmount || extractedData.premiumAmount || extractedData.originalAmount || '',
      dueDate: details.dueDate || extractedData.dueDate || '',
      policyStartDate: details.policyStartDate || extractedData.policyStartDate || '',
      policyEndDate: details.policyEndDate || extractedData.policyEndDate || '',
      repaymentFrequency: normalizeRepaymentFrequency(details.repaymentFrequency || extractedData.repaymentFrequency),
      deductibleAmount: details.deductibleAmount || extractedData.deductibleAmount || ''
    };
  },
  'personal debts': (extractedData) => {
    const details = extractedData.personalDebtsDetails || {};
    return {
      name: details.category ? `${details.category} - ${details.lenderName || ''}` : extractedData.name || '',
      lenderName: details.lenderName || extractedData.lenderName || '',
      originalAmount: details.originalAmount || extractedData.originalAmount || '',
      outstandingAmount: details.outstandingAmount || extractedData.outstandingAmount || '',
      interestRate: details.interestRate || extractedData.interestRate || '',
      dueDate: details.dueDate || extractedData.dueDate || '',
      category: normalizeDebtCategory(details.category || extractedData.category),
      repaymentFrequency: normalizeRepaymentFrequency(details.repaymentFrequency || extractedData.repaymentFrequency)
    };
  }
};

// Main function to fill extracted fields into form
export const fillLiabilityExtractedFields = (extractedItem, setFieldValue, liabilityType) => {
  if (!extractedItem || !setFieldValue) return {};
  const mapper = extractionMappers[liabilityType];
  if (!mapper) return {};
  const mappedData = mapper(extractedItem);
  Object.entries(mappedData).forEach(([fieldName, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      setFieldValue(fieldName, value);
    }
  });
  return {};
};

// Picker config for multiple records
export const LIABILITY_PICKER_CONFIG = {
  mortgage: {
    title: 'Select a Mortgage',
    name: (i) => `${i.lenderName || ''} - ${i.propertyAddress || ''}`,
    meta: (i) => `Amount: ${i.originalLoanAmount || '-'}, Rate: ${i.interestRate || '-'}%, Monthly: ${i.monthlyRepayment || '-'}`,
  },
  'vehicle loan': {
    title: 'Select a Vehicle Loan',
    name: (i) => `${i.vehicleName || i.vehicleType || ''}`,
    meta: (i) => `Amount: ${i.originalLoanAmount || '-'}, Lender: ${i.lenderName || '-'}, Monthly: ${i.monthlyRepayment || '-'}`,
  },
  loan: {
    title: 'Select a Loan',
    name: (i) => `${i.lenderName || ''} - ${i.loanPurpose || ''}`,
    meta: (i) => `Amount: ${i.originalAmount || '-'}, Rate: ${i.interestRate || '-'}%`,
  },
  'credit card': {
    title: 'Select a Credit Card',
    name: (i) => `${i.cardName || i.issuer || ''}`,
    meta: (i) => `Balance: ${i.currentBalance || '-'}, Limit: ${i.creditLimit || '-'}, APR: ${i.annualPercentageRate || '-'}%`,
  },
  overdraft: {
    title: 'Select an Overdraft',
    name: (i) => `${i.bankName || ''} - ${i.accountName || ''}`,
    meta: (i) => `Limit: ${i.overdraftLimit || '-'}, Balance: ${i.currentOverdraftAmount || '-'}`,
  },
  'tax payable': {
    title: 'Select a Tax Liability',
    name: (i) => `${i.taxType || ''} - ${i.taxYearPeriod || ''}`,
    meta: (i) => `Amount: ${i.amountDue || '-'}, Due: ${i.dueDate || '-'}`,
  },
  bnpl: {
    title: 'Select a BNPL Purchase',
    name: (i) => `${i.merchantName || ''} - ${i.purchaseDescription || ''}`,
    meta: (i) => `Total: ${i.totalAmount || '-'}, Remaining: ${i.remainingBalance || '-'}, Payments Left: ${i.remainingPayments || '-'}`,
  },
  'insurance premium': {
    title: 'Select an Insurance Premium',
    name: (i) => `${i.insuranceCompany || ''} - ${i.insuranceType || ''}`,
    meta: (i) => `Premium: ${i.premiumAmount || '-'}, Due: ${i.dueDate || '-'}, Frequency: ${i.repaymentFrequency || '-'}`,
  },
  'personal debts': {
    title: 'Select a Personal Debt',
    name: (i) => `${i.category || ''}`,
    meta: (i) => `Amount: ${i.originalAmount || '-'}, Remaining: ${i.outstandingAmount || '-'}`,
  }
};
